// src/components/OrganizationManagement.js

import React, { useState, useEffect } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { OrganizationService } from '../services/organizationService';
import { OrganizationTypes, getRolePermissions } from '../models/organizationModels';
import LoadingSpinner from './LoadingSpinner';

const OrganizationManagement = () => {
  const {
    currentOrganization,
    updateOrganization,
    isAdmin,
    userRole
  } = useOrganization();

  // State管理
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'members', 'invite', 'settings'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [members, setMembers] = useState([]);
  
  // 組織情報編集用
  const [organizationForm, setOrganizationForm] = useState({
    name: '',
    contact: {
      email: '',
      phone: '',
      address: ''
    }
  });

  // 招待フォーム
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'staff'
  });

  // 設定フォーム
  const [settingsForm, setSettingsForm] = useState({
    maxStaff: 100,
    timezone: 'Asia/Tokyo',
    language: 'ja',
    features: {}
  });

  // 初期化
  useEffect(() => {
    if (currentOrganization) {
      initializeForms();
      loadMembers();
    }
  }, [currentOrganization]);

  /**
   * フォームの初期化
   */
  const initializeForms = () => {
    setOrganizationForm({
      name: currentOrganization.name || '',
      contact: {
        email: currentOrganization.contact?.email || '',
        phone: currentOrganization.contact?.phone || '',
        address: currentOrganization.contact?.address || ''
      }
    });

    setSettingsForm({
      maxStaff: currentOrganization.settings?.maxStaff || 100,
      timezone: currentOrganization.settings?.timezone || 'Asia/Tokyo',
      language: currentOrganization.settings?.language || 'ja',
      features: currentOrganization.settings?.features || {}
    });
  };

  /**
   * メンバー一覧を読み込み
   */
  const loadMembers = async () => {
    try {
      setIsLoading(true);
      const result = await OrganizationService.getOrganizationMembers(
        currentOrganization.id,
        // userIdは認証コンテキストから取得する必要がありますが、
        // ここでは簡単のため仮の値を使用
        'current-user-id'
      );

      if (result.success) {
        setMembers(result.members);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('メンバー情報の読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 組織情報を更新
   */
  const handleUpdateOrganization = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError('');
      
      const result = await updateOrganization(currentOrganization.id, {
        name: organizationForm.name,
        contact: organizationForm.contact,
        updatedAt: new Date()
      });

      if (result.success) {
        setSuccess('組織情報を更新しました');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || '更新に失敗しました');
      }
    } catch (error) {
      setError('組織情報の更新中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 設定を更新
   */
  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError('');
      
      const result = await updateOrganization(currentOrganization.id, {
        settings: settingsForm,
        updatedAt: new Date()
      });

      if (result.success) {
        setSuccess('設定を更新しました');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || '設定の更新に失敗しました');
      }
    } catch (error) {
      setError('設定の更新中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * メンバーを招待
   */
  const handleInviteMember = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const result = await OrganizationService.inviteUser(
        currentOrganization.id,
        inviteForm.email,
        inviteForm.role,
        'current-user-id' // 実際の実装では認証コンテキストから取得
      );

      if (result.success) {
        setSuccess(`招待コードを生成しました: ${result.inviteCode}`);
        setInviteForm({ email: '', role: 'staff' });
        // 実際の実装では招待メールを送信
      } else {
        setError(result.error || '招待の送信に失敗しました');
      }
    } catch (error) {
      setError('招待の送信中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * フォーム更新ヘルパー
   */
  const updateOrgForm = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setOrganizationForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setOrganizationForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const updateSettingsForm = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setSettingsForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setSettingsForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // 管理者権限チェック
  if (!isAdmin()) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>アクセス権限がありません</h2>
          <p>組織管理機能を使用するには管理者権限が必要です。</p>
        </div>
      </div>
    );
  }

  if (!currentOrganization) {
    return (
      <div style={styles.container}>
        <LoadingSpinner message="組織情報を読み込み中..." />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <h1 style={styles.title}>組織管理</h1>
        <div style={styles.orgInfo}>
          <span style={styles.orgName}>{currentOrganization.name}</span>
          <span style={styles.orgType}>
            {OrganizationTypes[currentOrganization.type]?.name}
          </span>
        </div>
      </div>

      {/* 成功・エラーメッセージ */}
      {success && (
        <div style={styles.successAlert}>
          <span style={styles.alertIcon}>✅</span>
          {success}
        </div>
      )}
      
      {error && (
        <div style={styles.errorAlert}>
          <span style={styles.alertIcon}>⚠️</span>
          {error}
        </div>
      )}

      {/* タブナビゲーション */}
      <div style={styles.tabs}>
        {[
          { id: 'general', label: '基本情報', icon: '🏥' },
          { id: 'members', label: 'メンバー', icon: '👥' },
          { id: 'invite', label: '招待', icon: '✉️' },
          { id: 'settings', label: '設定', icon: '⚙️' }
        ].map(tab => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* タブコンテンツ */}
      <div style={styles.content}>
        {activeTab === 'general' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>基本情報</h2>
            <form onSubmit={handleUpdateOrganization} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>組織名 *</label>
                <input
                  type="text"
                  value={organizationForm.name}
                  onChange={(e) => updateOrgForm('name', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>メールアドレス</label>
                <input
                  type="email"
                  value={organizationForm.contact.email}
                  onChange={(e) => updateOrgForm('contact.email', e.target.value)}
                  style={styles.input}
                  placeholder="admin@hospital.com"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>電話番号</label>
                <input
                  type="tel"
                  value={organizationForm.contact.phone}
                  onChange={(e) => updateOrgForm('contact.phone', e.target.value)}
                  style={styles.input}
                  placeholder="03-1234-5678"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>住所</label>
                <textarea
                  value={organizationForm.contact.address}
                  onChange={(e) => updateOrgForm('contact.address', e.target.value)}
                  style={styles.textarea}
                  placeholder="〒000-0000 都道府県市区町村..."
                  rows="3"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  ...styles.submitButton,
                  ...(isLoading ? styles.disabledButton : {})
                }}
              >
                {isLoading ? '更新中...' : '基本情報を更新'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'members' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>メンバー管理</h2>
            
            {isLoading ? (
              <LoadingSpinner message="メンバー情報を読み込み中..." />
            ) : (
              <div style={styles.membersList}>
                {members.length === 0 ? (
                  <div style={styles.emptyState}>
                    <p>メンバーが見つかりません</p>
                  </div>
                ) : (
                  members.map((member, index) => (
                    <div key={index} style={styles.memberCard}>
                      <div style={styles.memberInfo}>
                        <div style={styles.memberName}>
                          {member.userId}
                        </div>
                        <div style={styles.memberRole}>
                          {getRoleDisplayName(member.role)}
                        </div>
                      </div>
                      <div style={styles.memberActions}>
                        <span style={styles.joinDate}>
                          参加: {new Date(member.joinedAt.seconds * 1000).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'invite' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>メンバー招待</h2>
            <form onSubmit={handleInviteMember} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>招待するメールアドレス *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                  style={styles.input}
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>権限レベル *</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                  style={styles.select}
                >
                  <option value="staff">スタッフ - 基本機能のみ</option>
                  <option value="manager">マネージャー - スケジュール管理可能</option>
                  <option value="admin">管理者 - すべての機能利用可能</option>
                </select>
              </div>

              <div style={styles.permissionInfo}>
                <h4>選択した権限で利用可能な機能:</h4>
                <ul style={styles.permissionList}>
                  {getRolePermissions(inviteForm.role).map((permission, index) => (
                    <li key={index} style={styles.permissionItem}>
                      {getPermissionDescription(permission)}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  ...styles.submitButton,
                  ...(isLoading ? styles.disabledButton : {})
                }}
              >
                {isLoading ? '招待送信中...' : '招待を送信'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>組織設定</h2>
            <form onSubmit={handleUpdateSettings} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>最大スタッフ数</label>
                <input
                  type="number"
                  value={settingsForm.maxStaff}
                  onChange={(e) => updateSettingsForm('maxStaff', parseInt(e.target.value))}
                  style={styles.input}
                  min="1"
                  max="1000"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>タイムゾーン</label>
                <select
                  value={settingsForm.timezone}
                  onChange={(e) => updateSettingsForm('timezone', e.target.value)}
                  style={styles.select}
                >
                  <option value="Asia/Tokyo">Asia/Tokyo (日本標準時)</option>
                  <option value="UTC">UTC (協定世界時)</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>言語</label>
                <select
                  value={settingsForm.language}
                  onChange={(e) => updateSettingsForm('language', e.target.value)}
                  style={styles.select}
                >
                  <option value="ja">日本語</option>
                  <option value="en">English</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  ...styles.submitButton,
                  ...(isLoading ? styles.disabledButton : {})
                }}
              >
                {isLoading ? '設定保存中...' : '設定を保存'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// ユーティリティ関数
const getRoleDisplayName = (role) => {
  const roleNames = {
    owner: 'オーナー',
    admin: '管理者',
    manager: 'マネージャー',
    staff: 'スタッフ'
  };
  return roleNames[role] || 'スタッフ';
};

const getPermissionDescription = (permission) => {
  const descriptions = {
    'org:read': '組織情報の閲覧',
    'org:write': '組織設定の変更',
    'org:manage_users': 'ユーザー管理',
    'staff:read': 'スタッフ情報の閲覧',
    'staff:write': 'スタッフ情報の編集',
    'staff:manage': 'スタッフの管理',
    'schedule:read': 'スケジュールの閲覧',
    'schedule:write': 'スケジュールの編集',
    'surgery:read': '手術スケジュールの閲覧',
    'surgery:write': '手術スケジュールの編集',
    'report:read': 'レポートの閲覧',
    'report:generate': 'レポートの生成',
    'settings:read': '設定の閲覧',
    'settings:write': '設定の変更'
  };
  return descriptions[permission] || permission;
};

// スタイル定義
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },

  header: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    marginBottom: '24px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
  },

  orgInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },

  orgName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2196f3',
  },

  orgType: {
    fontSize: '14px',
    color: '#666',
  },

  successAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#d4edda',
    border: '1px solid #c3e6cb',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    color: '#155724',
  },

  errorAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    marginBottom: '24px',
    fontSize: '14px',
    color: '#721c24',
  },

  alertIcon: {
    fontSize: '16px',
  },

  tabs: {
    display: 'flex',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '6px',
    marginBottom: '24px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    overflowX: 'auto',
  },

  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: '500',
    minWidth: 'fit-content',
  },

  activeTab: {
    backgroundColor: '#2196f3',
    color: 'white',
    boxShadow: '0 2px 4px rgba(33, 150, 243, 0.3)',
  },

  tabIcon: {
    fontSize: '16px',
  },

  content: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },

  section: {
    width: '100%',
  },

  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '24px',
    marginTop: 0,
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },

  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },

  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },

  input: {
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s ease',
    outline: 'none',
  },

  textarea: {
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s ease',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
  },

  select: {
    padding: '12px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },

  submitButton: {
    padding: '14px 24px',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    alignSelf: 'flex-start',
  },

  disabledButton: {
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
  },

  membersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  memberCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },

  memberInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },

  memberName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a1a1a',
  },

  memberRole: {
    fontSize: '14px',
    color: '#666',
    backgroundColor: '#e9ecef',
    padding: '2px 8px',
    borderRadius: '12px',
    alignSelf: 'flex-start',
  },

  memberActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  joinDate: {
    fontSize: '12px',
    color: '#999',
  },

  permissionInfo: {
    backgroundColor: '#f8f9fa',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },

  permissionList: {
    margin: '8px 0 0 0',
    paddingLeft: '20px',
  },

  permissionItem: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '4px',
  },

  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },

  accessDenied: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
};

export default OrganizationManagement;